import { Injectable, inject } from '@angular/core';
import { FailReason, KnowledgeProgressTracker, KnowledgeTrackerService } from '@dataclouder/ngx-knowledge';
import { KnowledgeUserState } from '@dataclouder/ngx-knowledge';
import { AudioStorage } from '@dataclouder/ngx-lessons';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { WordFormComponent } from 'src/app/pages/my-words/word-form/word-form.component';
import { ConfirmService } from 'src/app/shared/confirm/confirm.service';

import {
  DeletedData,
  LearningActivityType,
  ProgressResultsDto,
  ProgressWordEvaluation,
  RandomFilter,
  WordFilterCategory,
  WordsMetadataByStatus,
} from '../classes';
import { KnowledgeWordCoreService } from './knowledge-words-core.service';
import { LangWord, ValidNodeTables } from './lexicore.classes';
import { WordGenerationService } from './word-generation.service';
import { UserService } from '../data-services/user.service';
import { BusinessRule, Endpoints, InitWebApi, WordFormMode, WordWebApi } from '../enums';
import { KnowledgeWordStateService } from './knowledge-words-state.service';
import { WordsCoreStateManagerService } from './words-core-state-manager.service';
import { StoreUtilService } from '../store/store-util.service';
import { AudioService } from '../system/audio.service';
import { HttpService } from '../system/http.service';
import { ToastService } from '../system/toast.service';

export interface IWordQueryMultiBases {
  word: string;
  otherBases: { lang: string; objectPath: string; dbPath: string }[];
}

export interface WordSavedRes {
  word: LangWord;
  knowledgeState: KnowledgeUserState;
}

interface WordProgressTrack {
  word: string;
  sucess: boolean;
  points: number;
  failReason: FailReason;
  learningActivity: LearningActivityType;
}

@Injectable({
  providedIn: 'root',
})
export class WordService {
  private dialogService = inject(DialogService);

  private httpService = inject(HttpService);
  private toastrService = inject(ToastService);
  private confirmService = inject(ConfirmService);
  private audioService = inject(AudioService);
  private userService = inject(UserService);
  private wordGenerationService = inject(WordGenerationService);
  private wordsCoreStateManagerService = inject(WordsCoreStateManagerService);

  // Services for knowledge
  private knowledgeTrackerService = inject(KnowledgeTrackerService);
  private lexicoreKnowledgeService = inject(KnowledgeWordCoreService);
  private knowledgeWordStateService = inject(KnowledgeWordStateService);
  private wordsStateCoreService = inject(WordsCoreStateManagerService);

  public getWords$(): Observable<Array<LangWord>> {
    const langString = this.userService.getUserLangOptions();
    return this.httpService.getObservable<Array<LangWord>>(`${WordWebApi.GetUserWord}?${langString}`, 'nodejs');
  }

  public getRandomWords(
    n: number,
    by = WordFilterCategory.UseFrequency,
    value: any | any[],
    lang: { base: string; target: string } = null,
  ): Promise<Array<LangWord>> {
    const filter: RandomFilter = { nWords: n, by: by, values: value };
    const langString = lang ? `target=${lang.target}&base=${lang.base}` : this.userService.getUserLangOptions();
    return this.httpService.postDataToService(`${WordWebApi.RandomWord}?${langString}`, filter);
  }

  public async removeFromUserList(word: string): Promise<DeletedData> {
    const isConfirmed = confirm('Quieres eliminar la palabra, ya la sabias, crees que la aprendiste o quieres volverla a aprender despues');
    console.log('Esta pendiente esta solucion', isConfirmed);
    const langString = this.userService.getUserLangOptions();

    const response = await this.httpService.deleteDataFromService(`${WordWebApi.DeleteWord}/${word}?${langString}`);
    //TODO: Necesito saber que hacer en este caso.
    // await this.wordsCoreStateManagerService.removeWord(word);
    // this.store.dispatch(WordActions.deleteWordSuccess({ word }));
    return response;
  }

  // updateWordCore probably should be here and not in admin.

  // TODO: 🚨 This will be the only method to save words. refactor and use in all places.
  public async newSaveWordToVocabulary(word: string): Promise<WordSavedRes> {
    const langString = this.userService.getUserLangOptions();
    // 1) Validate not duplicated.
    const wordsState = await this.wordsStateCoreService.getStatusWords();
    const wordFound = wordsState[word];
    if (wordFound) {
      this.toastrService.warn(`Ya tienes registrado la palabra "${word}"`, `Estado: ${wordFound.status}`);
      return null;
    }
    // 2) Validate business rules. only for premium or follower users.
    const isFollowerOrBetaOrPremimPlan = this.userService.isFollowerOrBetaOrPremimPlan();
    if (!isFollowerOrBetaOrPremimPlan) {
      // Additional validations for business rules.
      if (Object.keys(wordsState).length >= BusinessRule.MaxWords) {
        const hasVocabulary = this.userService.hasVocabulariPermission();

        if (!hasVocabulary) {
          this.toastrService.warn(
            `Ya tienes el máximo de palabras gratis`,
            'Pero revisa las redes sociales, encontrarás un codigo para registrar palabras ilimitadas',
          );
          return null;
        }
      }
    }
    const res = await this.httpService.postDataToService<WordSavedRes>(`${WordWebApi.SaveWordWithString}?${langString}`, { word });
    await this.wordsCoreStateManagerService.updateCoreState(res.word, res.knowledgeState);
    return res;
  }

  public async saveMultipleUserWords(wordsStatus: WordsMetadataByStatus) {
    try {
      const langString = this.userService.getUserLangOptions();
      const response: { savedWords: LangWord[]; removedFromRecommended: string[]; words: LangWord[]; state: any[] } = await this.httpService.postDataToService(
        `${WordWebApi.SaveMultipleWordsStatus}?${langString}`,
        wordsStatus,
      );

      if (response?.words?.length > 0) {
        await this.lexicoreKnowledgeService.updateMemoryAndDB(this.userService.getTargetLanguage(), response.words);
        // UPDATE THE STATE AND DB.
      }
      if (response?.state?.length > 0) {
        console.log('state', response.state);
        await this.knowledgeWordStateService.updateMemoryAndDB(this.userService.getTargetLanguage(), response.state);
      }

      const recommendations: any = cloneDeep(this.userService.getUserSnapshot().recommendations); // solo quiero actualizar recommendations.words.words pero no tengo un método para hacerlo. por eso clono el objeto y lo actualizo.
      // filter recomended removing response.removedFromRecommended
      recommendations.words.words = recommendations.words.words.filter((word) => !response.removedFromRecommended.includes(word.word));
      this.userService.updateOnlyDispatchUserState({ recommendations: recommendations });
      const wordsSaved = response.savedWords.map((word) => word.word);

      this.toastrService.success(`Entendido, guardamos tu progreso`, `Pronto aprenderás las palabras: ${wordsSaved}`);
      return response;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async promoteWord(word: LangWord): Promise<LangWord> {
    return await this.httpService.postDataToService(WordWebApi.PromoteWord, word);
  }

  // ♦️ This is the new method.
  public async saveProgressTrackWord(progressTrack: KnowledgeProgressTracker): Promise<KnowledgeProgressTracker> {
    const lang = this.userService.getTargetLanguage();
    const tableName = `knowledgeState.${lang}.word` as ValidNodeTables;
    const result = await this.knowledgeTrackerService.saveProgress<ValidNodeTables>(progressTrack, tableName);
    return result;
  }

  public async saveProgressWord(progressWords: Array<ProgressWordEvaluation>, callbackFn: () => void = null): Promise<Array<ProgressWordEvaluation>> {
    const langString = this.userService.getUserLangOptions();
    const targetLang = this.userService.getUserSnapshot().settings.targetLanguage;
    const responseProgress = await this.httpService.postDataToService<ProgressResultsDto>(`${WordWebApi.SaveProgress}?${langString}`, progressWords);

    // Convert ProgressResultsDto to KnowledgeProgressTracker
    // ❌ TODO: hacer parcial y ver que elementos no se necesitan.
    const progress: KnowledgeProgressTracker[] = responseProgress.progress.map((progress: ProgressWordEvaluation) => {
      return {
        conceptId: progress.word,
        concept: progress.word,
        conceptType: `${targetLang}.word`,
        conceptPath: `${targetLang}.word.${progress.word}`,
        progress: progress.progress,
        points: progress.points || 0,
        learningActivity: LearningActivityType.WordFlashCard,
        activityId: null,
        success: progress.ok,
        revealed: progress.revealed,
        completed: progress.completed,
        createdAt: new Date(),
      };
    });
    // Fix this in future versión, only knowledgeService can save data.

    // this.store.dispatch(WordActions.updateProgressWord({ progress: responseProgress.progress }));

    if (responseProgress.points > 0) {
      const points = this.userService.getUserSnapshot().stats.points;

      const rightMostDigit = points % 10;
      const n_coints = Math.floor((rightMostDigit + responseProgress.points) / 10);
      console.log('n_coints', n_coints, 'points', responseProgress.points, 'right points', rightMostDigit);
      this.audioService.playCoins(n_coints);

      // this.store.dispatch(UserActions.addStatPoints({ points: responseProgress.points }));
    }

    const data = responseProgress.progress.filter((word) => word.justCompleted);
    if (data.length > 0) {
      let title = '';
      let message = '';

      if (data.length === 1) {
        title = `Felicidades completaste el progreso para la palabra ${data[0].word}.`;
        message = `¿Quieres eliminar ${data[0].word} de tu lista de aprendizaje ?`;
      } else {
        title = `Felicidades completaste el progreso para las palabras ${data.map((item) => item.word).join(', ')}.`;
        message = `¿Quieres eliminar ${data.map((item) => item.word).join(', ')} de tu lista de aprendizaje ?`;
      }

      const ref = this.confirmService.openConfirm(title, message, 'Eliminar');

      ref.subscribe(async (result) => {
        console.log('subs Eliminar palabra', result);

        if (result) {
          for (const word of data) {
            await this.removeFromUserList(word.word);
            if (callbackFn) {
              callbackFn();
            }
          }
        }
      });
    }

    return responseProgress.progress;
  }

  public async updateWordProperty(word: string, property: string, value: any): Promise<void> {
    // This method asumes backend is already updated so need to update memory to avoid refresh.
    // TODO: i need to test this. should work.

    const targetLang = this.userService.getTargetLanguage();
    const wordCore = await this.lexicoreKnowledgeService.getWord(targetLang, word);
    await this.lexicoreKnowledgeService.updateMemoryAndDB(targetLang, [{ ...wordCore, [property]: value }]);
  }

  public isWordInUserList(word: string): boolean {
    const isFound = this.wordsCoreStateManagerService.isWordInState(word);
    return !!isFound;
  }

  public async getSingleWord(word: string): Promise<LangWord> {
    const targetLang = this.userService.getTargetLanguage();
    const langString = this.userService.getUserLangOptions();
    const wordCore = await this.lexicoreKnowledgeService.getWord(targetLang, word);
    if (wordCore) {
      return wordCore;
    } else {
      const newWord = await this.httpService.getDataFromService<LangWord>(`${WordWebApi.GetWord}/${word}?${langString}`);
      await this.lexicoreKnowledgeService.updateMemoryAndDB(targetLang, [{ ...newWord }]);
      return newWord;
    }
  }

  public async getWordQueryBases(word: string): Promise<LangWord> {
    const langString = this.userService.getUserLangOptions();
    const data: IWordQueryMultiBases = { word, otherBases: [{ lang: 'ja', objectPath: 'es.translation', dbPath: 'es.translation' }] };
    return this.httpService.postDataToService(`${WordWebApi.QueryWordBases}?${langString}`, data);
  }

  public openWordFormDialog(word: LangWord, topicId = null, mode: WordFormMode = null): void {
    const context = { englishWord: word, topicId, mode };
    console.log(context);
    const dialogRef = this.dialogService.open(WordFormComponent, {
      data: context,
      closable: true,
    });

    dialogRef.onClose.subscribe((_) => {});
  }

  public async updateRecommendationsWords() {
    const response = await this.httpService.getDataFromService<{ updatedDate: string; words: any }>(InitWebApi.UpdateRecommendations);
    console.log('response', response);
    const recommendations = cloneDeep(this.userService.getUserSnapshot().recommendations); // solo quiero actualizar recommendations.words.words pero no tengo un método para hacerlo. por eso clono el objeto y lo actualizo.
    recommendations.words.words = response.words;
    this.userService.updateOnlyDispatchUserState({ recommendations });

    console.log('updateRecommendationsWords', response);
  }

  public async generateWordAudio(word: string, codeLang: string) {
    const audio = await this.httpService.getDataFromService<AudioStorage>(Endpoints.Lexicore.Generation.Audio + `/${word}?lang=${codeLang}`);
    return audio;
  }

  buildProgressTrack(progressTrack: WordProgressTrack): KnowledgeProgressTracker {
    const targetLang = this.userService.getUserSnapshot().settings.targetLanguage;

    return {
      conceptId: progressTrack.word,
      concept: progressTrack.word,
      conceptType: `${targetLang}.word`,
      conceptPath: `${targetLang}.word.${progressTrack.word}`,
      points: progressTrack.points,
      learningActivity: progressTrack.learningActivity,
      activityId: null,
      success: progressTrack.sucess,
      failReason: progressTrack.failReason,
      createdAt: new Date(),
    };
  }

  // NEW METHODS FOR KNOLEDGE SYSTEM

  public async refreshWordsCoreAndState(): Promise<void> {
    this.wordsCoreStateManagerService.refreshCoreState();
  }
}
